/**
 * Teams Hub Global State Store
 * 
 * Manages channels, messages, presence, and unread counts using Zustand.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Channel, User, Message } from '../lib/api'

interface TeamsState {
  // Active channel/conversation
  activeChannelId: string | null
  setActiveChannel: (id: string | null) => void
  
  // Channels cache
  channels: Channel[]
  setChannels: (channels: Channel[]) => void
  updateChannel: (id: string, updates: Partial<Channel>) => void
  
  // Users cache
  users: User[]
  setUsers: (users: User[]) => void
  updateUserPresence: (userId: number, isOnline: boolean, status?: string) => void
  
  // Unread counts
  totalUnreadChannels: number
  totalUnreadDMs: number
  setUnreadCounts: (channels: number, dms: number) => void
  
  // Draft messages (per channel)
  drafts: Record<string, string>
  setDraft: (channelId: string, content: string) => void
  clearDraft: (channelId: string) => void
  
  // UI preferences
  sidebarCollapsed: boolean
  toggleSidebar: () => void
  
  // Notification settings
  mutedChannels: string[]
  muteChannel: (id: string) => void
  unmuteChannel: (id: string) => void
  
  // Presence
  myStatus: 'online' | 'away' | 'busy' | 'offline'
  setMyStatus: (status: 'online' | 'away' | 'busy' | 'offline') => void
}

export const useTeamsStore = create<TeamsState>()(
  persist(
    (set, get) => ({
      // Active channel
      activeChannelId: null,
      setActiveChannel: (id) => set({ activeChannelId: id }),
      
      // Channels
      channels: [],
      setChannels: (channels) => set({ channels }),
      updateChannel: (id, updates) => set((state) => ({
        channels: state.channels.map(ch => 
          ch.id === id ? { ...ch, ...updates } : ch
        ),
      })),
      
      // Users
      users: [],
      setUsers: (users) => set({ users }),
      updateUserPresence: (userId, isOnline, status) => set((state) => ({
        users: state.users.map(u => 
          u.id === userId ? { ...u, is_online: isOnline, status: status || (isOnline ? 'online' : 'offline') } : u
        ),
      })),
      
      // Unread counts
      totalUnreadChannels: 0,
      totalUnreadDMs: 0,
      setUnreadCounts: (channels, dms) => set({ 
        totalUnreadChannels: channels, 
        totalUnreadDMs: dms 
      }),
      
      // Drafts
      drafts: {},
      setDraft: (channelId, content) => set((state) => ({
        drafts: { ...state.drafts, [channelId]: content },
      })),
      clearDraft: (channelId) => set((state) => {
        const { [channelId]: _, ...rest } = state.drafts
        return { drafts: rest }
      }),
      
      // UI
      sidebarCollapsed: false,
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      
      // Muted
      mutedChannels: [],
      muteChannel: (id) => set((state) => ({
        mutedChannels: [...state.mutedChannels, id],
      })),
      unmuteChannel: (id) => set((state) => ({
        mutedChannels: state.mutedChannels.filter(c => c !== id),
      })),
      
      // Presence
      myStatus: 'online',
      setMyStatus: (status) => set({ myStatus: status }),
    }),
    {
      name: 'teams-hub-storage',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        mutedChannels: state.mutedChannels,
        drafts: state.drafts,
      }),
    }
  )
)
